import { ContactLookupSettings } from "@signalist/shared-types";

import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";
import { SelectField } from "@/components/SelectField/SelectField";

import { ListPreview, ListPreviewStatus } from "../schema/listPreview";
import { PreviewExtendCreditConfirm } from "./PreviewExtendCreditConfirm";

interface PreviewOutcomeFooterProps {
	preview: ListPreview;
	/** The list's contact lookup settings, which an extension is quoted for. */
	contactLookups: ContactLookupSettings;
	onRerun: () => void;
	/** Starts the extension; rejects when the API refuses it, which the confirm then shows. */
	onExtend: (additionalCount: 10 | 20 | 50) => Promise<unknown>;
	isExtendLoading: boolean;
}

type ExtendCount = 10 | 20 | 50;

const EXTEND_COUNT_OPTIONS = [
	{ title: "10 companies", value: 10 },
	{ title: "20 companies", value: 20 },
	{ title: "50 companies", value: 50 },
];

export const PreviewOutcomeFooter: FunctionComponent<PreviewOutcomeFooterProps> = ({
	preview,
	contactLookups,
	onRerun,
	onExtend,
	isExtendLoading,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`list.preview.${key}`);
	const [extendCount, setExtendCount] = useState<ExtendCount>(20);
	const [isConfirmingExtend, setIsConfirmingExtend] = useState(false);

	const isCompleted = preview.status === ListPreviewStatus.COMPLETED;
	const isStopped = preview.status === ListPreviewStatus.STOPPED;

	return (
		<div className="mt-4 rounded-lg border border-border-light bg-brand-white p-4">
			<div className="flex items-center gap-2 mb-3">
				<div
					className={`h-2 w-2 rounded-full ${isCompleted ? "bg-status-success" : "bg-status-warning"}`}
				/>
				<span className="text-sm font-medium text-text">
					{isCompleted ? ts("completed") : isStopped ? ts("stopped") : ts("failed")}
				</span>
			</div>

			{/* Stats summary */}
			<div className="flex items-center gap-6 mb-4">
				<div className="text-xs text-text-meta">
					{ts("found")}: <span className="font-medium text-text">{preview.foundCount}</span>
				</div>
				<div className="text-xs text-text-meta">
					{ts("unsure")}: <span className="font-medium text-text">{preview.unsureCount}</span>
				</div>
				<div className="text-xs text-text-meta">
					{ts("notFound")}: <span className="font-medium text-text">{preview.notFoundCount}</span>
				</div>
				<div className="text-xs text-text-meta">
					{ts("creditsUsed")}:{" "}
					<span className="font-medium text-text">
						{preview.foundCount + preview.unsureCount + preview.notFoundCount}
					</span>
				</div>
				{preview.estimatedFullListCredits !== null && (
					<div className="text-xs text-text-meta">
						{ts("estimatedFullListCredits")}:{" "}
						<span className="font-medium text-text">{preview.estimatedFullListCredits}</span>
					</div>
				)}
			</div>

			{/* Action buttons */}
			<div className="flex items-center gap-4">
				<Button color={ButtonColor.ACTION_SECONDARY} title={ts("rerunPreview")} onClick={onRerun} />
				<div className="h-8 border-l border-border-light" />
				<div className="flex items-center gap-2">
					<div className="w-48">
						<SelectField
							handleSelect={(value) => setExtendCount(value as ExtendCount)}
							hideEmptyValue
							options={EXTEND_COUNT_OPTIONS}
							portal
							selectedOption={extendCount}
						/>
					</div>
					<Button
						isLoading={isExtendLoading}
						title={ts("extendPreview")}
						onClick={() => setIsConfirmingExtend(true)}
					/>
				</div>
			</div>

			{isConfirmingExtend && (
				<PreviewExtendCreditConfirm
					additionalCount={extendCount}
					contactLookups={contactLookups}
					onClose={() => setIsConfirmingExtend(false)}
					onConfirm={async () => {
						await onExtend(extendCount);
						setIsConfirmingExtend(false);
					}}
				/>
			)}
		</div>
	);
};
